/**
 * Mock IVTHub client — a fake that satisfies the `.send(command)` surface the
 * pages use, dispatching by command class name to the typed fixtures.
 *
 * Why this shape: every IVT page calls `getIvtClient().send(new XxxCommand({}))`.
 * By returning an object whose `.send()` reads `command.constructor.name` and
 * returns the matching fixture, we let pages run UNCHANGED while the real
 * backend op/endpoint does not exist. This is the swap seam that lets the
 * frontend run ahead of the service (the stated AIDLC goal: "use hardcoded mock
 * data while the service API is not ready").
 *
 * It is intentionally minimal — no SigV4, no network. Used in two places:
 *   1. component/page tests (jsdom) — deterministic render input
 *   2. local dev preview when VITE_IVT_MOCK=1 — see getIvtClient() in
 *      IvtHubClient.ts, which returns this instead of a live IVTHubClient.
 */

import {
  listAssetsMock,
  listIndustriesMock,
  getConfigurationMock,
  updateConfigurationMock,
  getAssetMock,
  getSolutionMapMock,
  getSolutionMapAllMock,
  listTopicsMock,
  upsertTopicMock,
  upsertTopicInstanceMock,
  getUcPipelineDetailMock,
  getUcFullMock,
  getPipelineBubblesMock,
  getPipelineBubblesAccountMock,
  getPipelineBubblesIndustryMock,
  updateAssetMock,
  linkUcAssetMock,
  upsertUcTagMock,
  manageArsenalMock,
  createMotionMock,
  listMotionsMock,
  listTaggedPipelineMock,
  getOppActivitiesMock,
  getMotionOverviewMock,
  getKbTreeMock,
  requestKbUploadUrlMock,
  requestKbOverviewUploadUrlMock,
  confirmKbUploadCompleteMock,
  getAssetAssessmentMock,
  listStorylinesMock,
  getStorylineMock,
  listAccountPipelineMock,
  listAssetOpportunitiesMock,
  listAccountFlagsMock,
  putOppAssetDismissalMock,
  deleteOppAssetDismissalMock,
  putAccountFlagMock,
  deleteAccountFlagMock,
  searchAccountsPool,
  industryGrantsMock,
  grantRosterMock,
  listRadarDirectionsFixtures,
  listRadarDirectionsMock,
  getRadarDirectionFixtures,
  getRadarDirectionMock,
  getAssetPipelineFlowMock,
  getIndustryPipelineFlowMock,
  radarConfigTopicsFixtures,
  radarConfigGeneralFixtures,
  radarConfigNewsSourcesFixtures,
} from './fixtures';
import type {
  IndustryGrants,
  ListAccountPipelineOutput,
  RadarStandardTopic,
  RadarGeneralTopic,
  RadarNewsSource,
  RadarStandardTopicDraft,
  RadarGeneralTopicDraft,
  RadarNewsSourceDraft,
  RadarConfigStatus,
} from '../client/IvtHubClient';
import type { AccountPipelineGroup as AccountGroup, AccountPipelineOpportunity as AccountOpp } from '@amzn/ivthub-client';
import { STAGE_BUCKETS as ACCOUNT_STAGE_BUCKETS } from '../pages/DashboardPage/accountPipelineFilter';

/** Latency (ms) to simulate in dev preview so loading states are visible. 0 in tests. */
export interface MockClientOptions {
  latencyMs?: number;
}

/** Map from command class name → fixture (or a function producing one). */
const RESPONSES: Record<string, unknown> = {
  // Admin configuration — lets /ivt/admin/configuration be driven locally while the
  // handlers are still in review (CR-2). The update fixture only advances the
  // revision; the mock cannot echo a request it never sees.
  GetConfigurationCommand: getConfigurationMock,
  UpdateConfigurationCommand: updateConfigurationMock,
  // Industry add / delete. References come back non-empty so the dialog's
  // "still in use" state is what mock mode shows.
  CreateIndustryCommand: {},
  GetIndustryReferencesCommand: { references: { asset: 3, motion: 1 } },
  DeleteIndustryCommand: {},
  ListAssetsCommand: listAssetsMock,
  ListIndustriesCommand: listIndustriesMock,
  GetAllowedIndustriesCommand: { industries: ['FSI', 'GAME', 'HCLS', 'RCH'] },
  GetAssetCommand: getAssetMock,
  // Dashboard flow stubs (R18 scope-adjacent) — without these, mock mode
  // crashes the dashboard before the radar section can render, which
  // blocks verifying any part of DashboardPage on fixtures.
  GetAssetPipelineFlowCommand: getAssetPipelineFlowMock,
  GetIndustryPipelineFlowCommand: getIndustryPipelineFlowMock,
  GetSolutionMapCommand: getSolutionMapMock,
  GetSolutionMapAllCommand: getSolutionMapAllMock,
  GetUcPipelineDetailCommand: getUcPipelineDetailMock,
  GetUcFullCommand: getUcFullMock,
  // GetPipelineBubbles now serves all four grains (industry / uc / asset /
  // account) that used to be split across GetUcPipeline, GetUcPipelineLive,
  // and GetAccountCrossUc. Dispatch by input.groupBy happens in send() below.
  GetPipelineBubblesCommand: getPipelineBubblesMock,
  GetAssetAssessmentCommand: getAssetAssessmentMock,
  // Write ops echo a minimal success so optimistic flows don't crash in mock mode.
  CreateAssetCommand: { asset: getAssetMock.asset },
  UpdateAssetCommand: updateAssetMock,
  ManageArsenalCommand: manageArsenalMock,
  UpdateElementCommand: { element: getAssetMock.asset?.elements?.[0] },
  DeleteAssetCommand: {},
  // UC Catalog arsenal edit (merged-asset write path)
  LinkUcAssetCommand: linkUcAssetMock,
  UnlinkUcAssetCommand: {},
  UpsertUcTagByIdCommand: upsertUcTagMock,
  // Topic ops (mock mode for SolutionMap "flip side" view — see topicClient.ts)
  ListTopicsCommand: listTopicsMock,
  UpsertTopicCommand: upsertTopicMock,
  UpsertTopicInstanceCommand: upsertTopicInstanceMock,
  DeleteTopicCommand: {},
  DeleteTopicInstanceCommand: {},
  // Topic↔Asset link (Topic-mode asset creation step 2). Echo a link ref so the
  // two-step create flow completes in mock mode.
  LinkTopicAssetCommand: { link: { subTopicId: 1001, assetId: 'MOCK-ASSET-1' } },
  // Motion (V14): ListMotions backs the Motions list page; CreateMotion echoes a
  // success for the Create dialog. Both handlers are implemented on mainline.
  ListMotionsCommand: listMotionsMock,
  GetOppActivitiesCommand: getOppActivitiesMock,
  CreateMotionCommand: createMotionMock,
  // UpdateMotion — echo the same Motion shape as CreateMotion for optimistic flows.
  UpdateMotionCommand: createMotionMock,
  // DeleteMotion (multi-select batch delete) — echo an empty success per id.
  DeleteMotionCommand: {},
  GetMotionOverviewCommand: getMotionOverviewMock,
  // KB read: owner-generic GetKbTree (replaces the retired List*KbFiles). Same
  // fixture data as folders[]+entries[]; serves both ASSET and UC owners in mock mode.
  GetKbTreeCommand: getKbTreeMock,
  // Overview first-save (owner-generic): request echoes a URL + deterministic id;
  // confirm echoes the confirmed Overview entry (reuse getKbTree's first entry shape).
  RequestKbOverviewUploadUrlCommand: requestKbOverviewUploadUrlMock,
  ConfirmKbOverviewUploadCommand: {
    entry: {
      fileId: 'MOCK-KB-OVERVIEW',
      entryType: 'file',
      sourceType: 'OVERVIEW',
      displayName: 'overview.md',
      contentType: 'text/markdown',
      status: 'uploaded',
    },
  },
  // Owner-generic KB file WRITE ops (one set, serves ASSET + UC). Confirm echoes
  // {entry: KbEntry}; DeleteKbEntry echoes empty (204). (The browser→S3 PUT is a plain
  // fetch, not a command — not mocked here; tests inject their own client for upload.)
  RequestKbFileUploadUrlCommand: requestKbUploadUrlMock,
  ConfirmKbFileUploadCommand: confirmKbUploadCompleteMock,
  DeleteKbEntryCommand: {},
  RequestKbFileEditUrlCommand: { editUrl: 'https://s3.example/kb-edit-put?sig=mock', expiresInSeconds: 900 },
  ConfirmKbFileEditGenericCommand: confirmKbUploadCompleteMock,
  GetKbFileDownloadUrlGenericCommand: { downloadUrl: 'https://s3.example/kb-get?sig=mock', expiresInSeconds: 60 },
  // URL entries (link, not a file). Add/Update echo {entry: KbEntry} (entryType='url',
  // sentinel status/contentType per the backend contract).
  AddKbUrlEntryCommand: {
    entry: {
      fileId: 'MOCK-KB-URL-NEW', entryType: 'url', sourceType: 'user_upload',
      displayName: 'mock link', url: 'https://example.com', status: 'saved', contentType: 'text/uri-list',
    },
  },
  UpdateKbUrlEntryCommand: {
    entry: {
      fileId: 'MOCK-KB-URL-NEW', entryType: 'url', sourceType: 'user_upload',
      displayName: 'mock link', url: 'https://example.com', status: 'saved', contentType: 'text/uri-list',
    },
  },
  // Storyline (Menu tree + Create modal + detail page). ListStorylines backs the
  // tree; GetStoryline the detail page; Create/Update(Description)/Delete the modal +
  // detail edits. Create/Get echo a well-typed storyline so optimistic flows run in
  // mock mode (the id is synthetic — dev-only; real ids come from the backend).
  ListStorylinesCommand: listStorylinesMock,
  GetStorylineCommand: getStorylineMock,
  CreateStorylineCommand: { storyline: getStorylineMock.storyline },
  UpdateStorylineCommand: { storyline: getStorylineMock.storyline },
  UpdateStorylineDescriptionCommand: { storyline: getStorylineMock.storyline },
  DeleteStorylineCommand: {},
  // STO Homepage (DAT-9005). ListAccountPipeline and its filter options are answered in send()
  // below (DAT-9223): ONE fixture serves both blocks, with the property filter evaluated over it
  // and the reclaim block dropping an account left without pipeline, as the service does.
  // maxResults / nextToken are ignored, unlike ListTaggedPipelines which paginates for real.
  ListAssetOpportunitiesCommand: listAssetOpportunitiesMock,
  ListAccountFlagsCommand: listAccountFlagsMock,
  // Write ops: empty bodies. The pages re-read their list after a write, so mock mode
  // shows the PRE-write data again — a dismissal or a flag does not appear to stick.
  PutOppAssetDismissalCommand: putOppAssetDismissalMock,
  DeleteOppAssetDismissalCommand: deleteOppAssetDismissalMock,
  PutAccountFlagCommand: putAccountFlagMock,
  DeleteAccountFlagCommand: deleteAccountFlagMock,
  // The SFDC hierarchy catalog: the nodes the configuration fixture maps (so the configuration
  // page reads none of them as "not in the list") plus those MOCK_ACCOUNT_HIERARCHY assigns.
  ListHierarchyNodesCommand: {
    nodes: [
      { level: 'sh_l4', name: 'FSI' },
      { level: 'sh_l4', name: 'GAME' },
      { level: 'sh_l4', name: 'HCLS' },
      { level: 'sh_l4', name: 'RCH' },
      { level: 'sh_l5', name: 'MOCK FSI-INS' },
      { level: 'territory', name: 'MOCK-TERRITORY-EAST-01' },
    ],
  },
};

// ── SFDC property-filter mock support (DAT-8462) ─────────────────────────────

/** Fixed 7 stage buckets for GetPipelineFilterOptions(SFDC_STAGE) — value/label. */
const STAGE_BUCKET_OPTIONS = [
  { value: 'prospect', label: 'Prospect' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'techValidation', label: 'Technical Validation' },
  { value: 'bizValidation', label: 'Business Validation' },
  { value: 'committed', label: 'Committed' },
  { value: 'launched', label: 'Launched' },
  { value: 'others', label: 'Others' },
];

type PipelineRow = NonNullable<typeof listTaggedPipelineMock.items>[number];

/** Property-filter field → the row property it matches against. */
const FIELD_TO_ROW: Record<string, keyof PipelineRow> = {
  SFDC_STAGE: 'sfdcStage',
  ACCOUNT_NAME: 'accountName',
  OPP_NAME: 'oppName',
  OPP_ID: 'opptyId',
  OPP_OWNER: 'oppOwner',
  PIPELINE_SCORE: 'pipelineScore',
};

/** Identity fields searchable via GetPipelineFilterOptions (Stage excluded). */
const IDENTITY_FIELD_TO_ROW: Record<string, keyof PipelineRow> = {
  ACCOUNT_NAME: 'accountName',
  OPP_NAME: 'oppName',
  OPP_ID: 'opptyId',
  OPP_OWNER: 'oppOwner',
};

interface Condition {
  field: string;
  operator: 'in' | 'notIn';
  values: string[];
}
interface Group {
  operation: 'and' | 'or';
  conditions: Condition[];
}
interface PropertyTree {
  operation: 'and' | 'or';
  groups: Group[];
}

// SFDC_STAGE condition values are BUCKET KEYS ('committed'), but a row's sfdcStage
// is the raw display stage ('Committed' / 'Tech Validation'). The backend classifies
// raw stage → bucket before matching; the mock must too, else a Stage filter never
// matches. Map the row's raw stage to its bucket key via STAGE_BUCKET_OPTIONS labels
// (label 'Technical Validation' won't match raw 'Tech Validation', so also fold via
// normalizeSfdcStage's display form). Simple case-insensitive contains keyed by bucket.
const STAGE_RAW_TO_BUCKET: Array<[bucket: string, test: (s: string) => boolean]> = [
  // Closed Lost must be tested BEFORE 'committed' — 'closed won' is Committed, 'closed lost'
  // is its own bucket (DAT-9259). The fixtures seed a `sfdcStage: 'Closed Lost'` row, so
  // leaving this out would make a Closed Lost pill filter nothing.
  ['closedLost', (s) => /closed lost/i.test(s)],
  ['committed', (s) => /committed|closed won/i.test(s)],
  ['launched', (s) => /launched/i.test(s)],
  ['bizValidation', (s) => /b(usiness|iz) validation/i.test(s)],
  ['techValidation', (s) => /tech(nical)? validation/i.test(s)],
  ['qualified', (s) => /qualified/i.test(s)],
  ['prospect', (s) => /prospect/i.test(s)],
];
export function rawStageToBucket(raw: string): string {
  for (const [bucket, test] of STAGE_RAW_TO_BUCKET) if (test(raw)) return bucket;
  return 'others';
}

/**
 * Can this condition be evaluated against a fixture row at all? Unevaluable conditions are
 * REMOVED before evaluation (see applyPropertyFilter) rather than answered true or false —
 * answering either is wrong in one direction:
 *   · false filters every row out, which reads as "the filter worked and matched nothing";
 *   · true is vacuous under AND but POISONS an OR group, making the whole group match
 *     everything. That is the same divergence this module already avoids by dropping
 *     empty-conditions groups, so removal is the consistent treatment.
 *
 * Two things are unevaluable here:
 *   · a field with no FIELD_TO_ROW entry. The BOOL dimensions (IS_GREENFIELD / IS_BIG_BET /
 *     HAS_IVT_ACTIVITY / TAG_MATCHES_HIERARCHY) are filter-only — `PipelineListItem` carries
 *     none of them — and CREATED_DATE has no row field either.
 *   · a date COMPARISON operator (lt/lte/gt/gte). `evalCondition` is set-membership only, so
 *     even CLOSE_DATE, which the row DOES carry, cannot be range-compared here.
 * ⚠️ So a bool or date-range filter is INERT in mock mode, not merely unpinned. Row counts
 * for those dimensions can only be judged against a real stage.
 */
function canEvaluate(c: Condition): boolean {
  if (!FIELD_TO_ROW[c.field]) return false;
  return c.operator === 'in' || c.operator === 'notIn';
}

function evalCondition(row: PipelineRow, c: Condition): boolean {
  const rowKey = FIELD_TO_ROW[c.field]!;
  const raw = row[rowKey];
  let val = typeof raw === 'string' ? raw : undefined;
  // SFDC_STAGE: compare the row's stage as a BUCKET KEY (values are bucket keys).
  if (c.field === 'SFDC_STAGE' && val != null) val = rawStageToBucket(val);
  // PIPELINE_SCORE: the row value is a NUMBER (pipelineScore) but tree values are
  // numeric strings ("3") — stringify the row value so includes() matches.
  if (c.field === 'PIPELINE_SCORE') val = typeof raw === 'number' ? String(raw) : undefined;
  const isIn = val != null && c.values.includes(val);
  return c.operator === 'notIn' ? !isIn : isIn;
}

function evalGroup(row: PipelineRow, g: Group): boolean {
  if (!g.conditions.length) return true;
  return g.operation === 'or'
    ? g.conditions.some((c) => evalCondition(row, c))
    : g.conditions.every((c) => evalCondition(row, c));
}

/** Evaluate the (JSON-string) property tree against the fixture rows — the mock's
 *  equivalent of the backend SQL, so tests verify translate → filter end to end. */
function applyPropertyFilter(rows: PipelineRow[], param: unknown): PipelineRow[] {
  if (typeof param !== 'string' || !param) return rows;
  let tree: PropertyTree;
  try {
    tree = JSON.parse(param) as PropertyTree;
  } catch {
    return rows; // malformed → no constraint (backend would 400; mock is lenient)
  }
  // Drop UNEVALUABLE conditions first, then empty-conditions groups (the backend's
  // normalize() does the latter) — else an empty group evaluated as TRUE would diverge under
  // a top-level OR (it'd make the whole OR match everything), and so would an unevaluable
  // condition left in place answering true. After dropping, an all-empty tree = no constraint.
  const groups = (tree.groups ?? [])
    .map((g) => ({ ...g, conditions: (g.conditions ?? []).filter(canEvaluate) }))
    .filter((g) => g.conditions.length);
  if (!groups.length) return rows;
  return rows.filter((row) =>
    tree.operation === 'or' ? groups.some((g) => evalGroup(row, g)) : groups.every((g) => evalGroup(row, g)),
  );
}

/** Does a pipeline row fall within the IVT-concept filter scope? Mirrors the
 *  backend FILTER_WHERE (§4.6): a row matches an industries/useCases/assetIds
 *  filter if it INTERSECTS the selection (a row is multi-industry / multi-UC /
 *  multi-asset via its useCases[]/assetIds — see the fanout view). Empty/absent
 *  filter = no constraint on that dimension. Only the dimensions the fixtures
 *  carry data for are enforced (industries/useCases/assetIds); the mock ignores
 *  subTopicIds/motionIds/hasMotion here (no fixture coverage → no test value). */
function rowMatchesIvtScope(row: PipelineRow, input: Record<string, unknown>): boolean {
  const asStrArr = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
  const industries = asStrArr(input.industries);
  const useCases = asStrArr(input.useCases);
  const assetIds = asStrArr(input.assetIds);
  const rowUcs = row.useCases ?? [];
  if (industries.length && !rowUcs.some((u) => u.industry && industries.includes(u.industry))) return false;
  if (useCases.length && !rowUcs.some((u) => u.ucName && useCases.includes(u.ucName))) return false;
  if (assetIds.length && !(row.assetIds ?? []).some((a) => assetIds.includes(a))) return false;
  return true;
}

// ── STO account-block property filter mock support (DAT-9223) ───────────────

/**
 * Each fixture account's own hierarchy nodes, as `<level>:<name>`. MOCKACCTLIGHT00002 has none:
 * the fixture has it outside account_cache, and such an account matches no node.
 */
const MOCK_ACCOUNT_HIERARCHY: Record<string, string[]> = {
  MOCKACCTLIGHT00001: ['sh_l4:FSI', 'sh_l5:MOCK FSI-INS', 'territory:MOCK-TERRITORY-EAST-01'],
  MOCKACCTLIGHT00003: ['sh_l4:FSI', 'sh_l5:MOCK FSI-INS'],
};

/**
 * The block's buckets as the service has them (AccountPipelineStageBuckets): an exact match on
 * the trimmed stage, Completed under launched, Closed Lost its own, anything else — including
 * the fixture's `Tech Validation`, which is not the canonical spelling — under others.
 */
const ACCOUNT_STAGE_BY_RAW: Readonly<Record<string, string>> = {
  Prospect: 'prospect',
  Qualified: 'qualified',
  'Technical Validation': 'techValidation',
  'Business Validation': 'bizValidation',
  Committed: 'committed',
  Launched: 'launched',
  Completed: 'launched',
  'Closed Lost': 'closedLost',
};
const accountStageBucket = (raw: string | undefined) => ACCOUNT_STAGE_BY_RAW[raw?.trim() ?? ''] ?? 'others';

/**
 * Mock-only rows, so the blocks' default (Launched / Completed and Closed Lost hidden) visibly
 * hides something in mock mode: the shared fixture has none of those stages, and other suites
 * count its rows.
 */
const MOCK_CLOSED_OPPS: AccountOpp[] = [
  {
    opptyId: 'MOCK-OPP-4105',
    oppName: 'MOCK 已上线的理赔项目',
    arr: 1_500_000,
    sfdcStage: 'Launched',
    hasIvtActivity: true,
    taggedAssets: [],
    suggestions: [{ assetId: 'fsi-claims-auto', assetName: 'MOCK 智能理赔自动化资产', relevanceScore: 4, isDismissed: false }],
  },
  {
    opptyId: 'MOCK-OPP-4106',
    oppName: 'MOCK 丢单的核心系统',
    arr: 700_000,
    sfdcStage: 'Closed Lost',
    hasIvtActivity: false,
    taggedAssets: [],
    suggestions: [{ assetId: 'fsi-fraud-detection', relevanceScore: 3, isDismissed: false }],
  },
];

const OPP_FIELDS: ReadonlySet<string> = new Set(['OPP_NAME', 'SFDC_STAGE']);

interface AccountRowContext {
  accountName?: string;
  hierarchy: string[];
  oppName?: string;
  stage?: string;
}

/**
 * The service's rule, on the fixture: an opportunity counts when it matches the whole tree; a
 * lighthouse account with none still shows when the tree holds with every opportunity
 * condition taken as met (`oppAsMet`).
 */
function evalAccountTree(tree: PropertyTree, row: AccountRowContext, oppAsMet: boolean): boolean {
  const cond = (c: Condition): boolean => {
    if (oppAsMet && OPP_FIELDS.has(c.field)) return true;
    const values: string[] =
      c.field === 'ACCOUNT_NAME'
        ? [row.accountName ?? '']
        : c.field === 'OPP_NAME'
          ? [row.oppName ?? '']
          : c.field === 'SFDC_STAGE'
            ? [accountStageBucket(row.stage)]
            : c.field === 'ACCOUNT_HIERARCHY'
              ? row.hierarchy
              : [];
    const hit = values.some((v) => c.values.includes(v));
    return c.operator === 'notIn' ? !hit : hit;
  };
  const group = (g: Group) => (g.operation === 'or' ? g.conditions.some(cond) : g.conditions.every(cond));
  const groups = (tree.groups ?? []).filter((g) => (g.conditions ?? []).length > 0);
  if (groups.length === 0) return true;
  return tree.operation === 'or' ? groups.some(group) : groups.every(group);
}

/**
 * Every header and `filteredTotals` is recounted from the rows, with or without a filter, so the
 * title moves only when the filter does. `coverage` stays the fixture's: it stands for a scope
 * larger than this one page, as on the service.
 */
/** The fixture with MOCK_CLOSED_OPPS under its first account — what both mock ops read. */
function mockAccountBlock(): ListAccountPipelineOutput {
  const full = structuredClone(listAccountPipelineMock);
  const first = full.accounts?.[0];
  if (first) first.untagged = [...(first.untagged ?? []), ...structuredClone(MOCK_CLOSED_OPPS)];
  return full;
}

function listAccountPipeline(input: Record<string, unknown>): ListAccountPipelineOutput {
  const full = mockAccountBlock();
  const param = input.propertyFilter;
  let tree: PropertyTree = { operation: 'and', groups: [] };
  try {
    if (typeof param === 'string' && param) tree = JSON.parse(param) as PropertyTree;
  } catch {
    // the service would 400; the mock is lenient
  }
  const lighthouse = input.lighthouse === true;
  const sum = (opps: AccountOpp[]) => ({
    oppCount: opps.length,
    arr: opps.reduce((n, o) => n + (o.arr ?? 0), 0),
  });
  const accounts: AccountGroup[] = [];
  for (const g of full.accounts ?? []) {
    const base = {
      accountName: g.account?.accountName,
      hierarchy: MOCK_ACCOUNT_HIERARCHY[g.account?.accountId ?? ''] ?? [],
    };
    const keep = (o: AccountOpp) => evalAccountTree(tree, { ...base, oppName: o.oppName, stage: o.sfdcStage }, false);
    const tagged = (g.tagged ?? []).filter(keep);
    const untagged = (g.untagged ?? []).filter(keep);
    const listed =
      tagged.length + untagged.length > 0 || (lighthouse && evalAccountTree(tree, base, true));
    if (!listed) continue;
    // Every fixture group has its header; the generated type only says it might not.
    accounts.push({ ...g, account: { ...g.account!, tagged: sum(tagged), untagged: sum(untagged) }, tagged, untagged });
  }
  const tagged = accounts.flatMap((a) => a.tagged ?? []);
  const untagged = accounts.flatMap((a) => a.untagged ?? []);
  return {
    ...full,
    accounts,
    filteredTotals: { accountCount: accounts.length, tagged: sum(tagged), untagged: sum(untagged) },
  };
}

/** Names over the fixture's block, substring + offset paging; the stage buckets whole. */
function listAccountPipelineFilterOptions(input: Record<string, unknown>): unknown {
  const field = String(input.field ?? '');
  if (field === 'SFDC_STAGE') return { items: ACCOUNT_STAGE_BUCKETS.map((b) => ({ ...b })) };
  const groups = mockAccountBlock().accounts ?? [];
  const names =
    field === 'ACCOUNT_NAME'
      ? groups.map((g) => g.account?.accountName)
      : field === 'OPP_NAME'
        ? groups.flatMap((g) => [...(g.tagged ?? []), ...(g.untagged ?? [])].map((o) => o.oppName))
        : [];
  const term = typeof input.searchTerm === 'string' ? input.searchTerm.trim().toLowerCase() : '';
  const distinct = [...new Set(names.filter((v): v is string => !!v))]
    .filter((v) => !term || v.toLowerCase().includes(term))
    .sort();
  const maxResults = typeof input.maxResults === 'number' && input.maxResults > 0 ? input.maxResults : 50;
  const start = typeof input.nextToken === 'string' ? Number.parseInt(input.nextToken, 10) || 0 : 0;
  const page = distinct.slice(start, start + maxResults);
  return {
    items: page.map((v) => ({ value: v, label: v })),
    nextToken: start + maxResults < distinct.length ? String(start + maxResults) : undefined,
  };
}

/**
 * Minimal stand-in shaped like the part of IVTHubClient the pages use.
 * Typed as `unknown`-returning by command; pages already treat outputs as the
 * generated types via the Command generics, and tsc checks the fixtures.
 */
export class MockIvtHubClient {
  private readonly latencyMs: number;
  // Stateful, unlike RESPONSES: the Access tab reloads after a write and must see it.
  private grants: IndustryGrants[] = structuredClone(industryGrantsMock);
  // R19: direction ids this client has seen UpdateRadarDirectionStatus for.
  // Kept here, NOT on the module-level fixtures, so sibling tests in the
  // same vitest worker and sibling tabs in dev-mode cannot see each
  // other's dismissals. `ListRadarDirectionsCommand` filters by this on
  // read.
  private dismissedDirectionIds: Set<string> = new Set();
  // Radar Admin (R16) — stateful per industry so VITE_IVT_MOCK=1 can round-trip
  // Upsert/SetStatus against the Admin page without the real backend. One map
  // per shape; INACTIVE rows stay in the map (soft delete) so the ListIndustry*
  // dispatcher can honor the optional status filter the way the service does.
  private radarTopics: Record<string, RadarStandardTopic[]> = structuredClone(
    radarConfigTopicsFixtures,
  );
  private radarGeneral: Record<string, RadarGeneralTopic | null> = structuredClone(
    radarConfigGeneralFixtures,
  );
  private radarNewsSources: Record<string, RadarNewsSource[]> = structuredClone(
    radarConfigNewsSourcesFixtures,
  );
  private radarIdCounter = 0;

  constructor(opts: MockClientOptions = {}) {
    this.latencyMs = opts.latencyMs ?? 0;
  }

  async send(command: { constructor: { name: string }; input?: Record<string, unknown> }): Promise<unknown> {
    const name = command.constructor.name;
    if (this.latencyMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, this.latencyMs));
    }
    // ListTaggedPipelines is @paginated — honor maxResults/nextToken so mock mode
    // and tests exercise real paging. Cursor = a plain offset ("<n>"); stageCounts is
    // returned in FULL on every page (铁律②). It is the tag-anchored v2 (DAT-8462).
    if (name === 'ListTaggedPipelinesCommand') {
      return this.paginateMotionPipeline(command.input ?? {});
    }
    if (name === 'GetPipelineFilterOptionsCommand') {
      return this.getPipelineFilterOptions(command.input ?? {});
    }
    if (name === 'ListAccountPipelineCommand') {
      return listAccountPipeline(command.input ?? {});
    }
    // ListRadarDirections (DAT-9115, R17): resolve by industry so the dev-mode
    // preview can hop between Figma A (multi-topic), C (general-only) and D
    // (empty-state) just by picking a different industry in the selector. An
    // unknown industry — or a request with no industry at all — falls through
    // to the HCLS fixture so the first paint still renders a rich card grid.
    if (name === 'ListRadarDirectionsCommand') {
      const industry = (command.input?.industry as string | undefined) ?? '';
      const base = listRadarDirectionsFixtures[industry] ?? listRadarDirectionsMock;
      const cloned = structuredClone(base);
      // R19: filter out per-CLIENT dismissed/adopted ids so a dismiss
      // visibly removes the card. The dashboard scopes this call to
      // CANDIDATE, which never contains dismissed/adopted directions on
      // the real server.
      if (this.dismissedDirectionIds.size > 0) {
        cloned.directions = (cloned.directions ?? []).filter(
          (d) => d.directionId === undefined || !this.dismissedDirectionIds.has(d.directionId),
        );
      }
      return cloned;
    }
    // GetRadarDirection (DAT-9115, R18): resolve by directionId so a dev-
    // mode click on any card title in the list fixtures above lands on the
    // matching detail state. An unknown id falls through to the HCLS rich
    // fixture so first-click never shows an empty modal.
    if (name === 'GetRadarDirectionCommand') {
      const id = (command.input?.directionId as string | undefined) ?? '';
      return structuredClone(getRadarDirectionFixtures[id] ?? getRadarDirectionMock);
    }
    // UpdateRadarDirectionStatus (DAT-9115, R19). Mark the direction as
    // dismissed in a per-CLIENT override map so a subsequent
    // ListRadarDirections (fired by the dashboard's reload) returns the
    // list minus this row — the card visibly drops off, which is the only
    // bit of this flow dev-mode needs to prove. The override lives on
    // THIS client instance (`this.dismissedDirectionIds`) and never
    // mutates the module-level `listRadarDirectionsFixtures`, so tests
    // in the same vitest worker and sibling tabs in dev-mode don't
    // interfere with each other. `ListRadarDirectionsCommand` applies
    // the override on read (above).
    //
    // The echoed `direction` mirrors GetRadarDirection's shape (per the
    // smithy, the Update op returns the updated direction) so a caller
    // that keyed off the response does not see a stale status.
    if (name === 'UpdateRadarDirectionStatusCommand') {
      const input = command.input ?? {};
      const id = (input.directionId as string | undefined) ?? '';
      const status = (input.status as string | undefined) ?? 'CANDIDATE';
      this.dismissedDirectionIds.add(id);
      const existing = getRadarDirectionFixtures[id] ?? getRadarDirectionMock;
      const direction = existing.direction
        ? { ...existing.direction, status }
        : undefined;
      return structuredClone({ direction });
    }
    if (name === 'ListAccountPipelineFilterOptionsCommand') {
      return listAccountPipelineFilterOptions(command.input ?? {});
    }
    if (name === 'ListIndustryGrantsCommand') {
      const alias = (command.input?.alias as string | undefined)?.toLowerCase();
      const industries = alias
        ? this.grants
            .map((g) => ({ ...g, grantees: (g.grantees ?? []).filter((x) => x.alias === alias) }))
            .filter((g) => g.grantees.length > 0)
        : this.grants;
      return structuredClone({ industries });
    }
    if (name === 'CreateIndustryGrantsCommand') {
      return this.createGrants(command.input ?? {});
    }
    if (name === 'DeleteIndustryGrantCommand') {
      const { industry, alias } = (command.input ?? {}) as { industry?: string; alias?: string };
      const row = this.grants.find((g) => g.industry === industry);
      if (row) row.grantees = (row.grantees ?? []).filter((x) => x.alias !== alias?.toLowerCase());
      return {};
    }
    if (name === 'SearchAccountsCommand') {
      return this.searchAccounts(command.input ?? {});
    }
    // Radar Admin (R16) — the 8 ops behind /ivt/radar-config. Stateful per
    // industry: a List call reads whatever Upsert/SetStatus wrote earlier
    // in this session, which is what makes the Admin page's Confirm cycle
    // observable in VITE_IVT_MOCK=1 mode. The 10 STANDARD_TOPIC/industry
    // cap mirrors the handler (topic-model §3.2) so the Add button and the
    // tests see the same ServiceException the real backend returns.
    if (name === 'ListIndustryTopicsCommand') {
      return this.listIndustryTopics(command.input ?? {});
    }
    if (name === 'GetIndustryTopicCommand') {
      return this.getIndustryTopic(command.input ?? {});
    }
    if (name === 'UpsertIndustryTopicCommand') {
      return this.upsertIndustryTopic(command.input ?? {});
    }
    if (name === 'SetIndustryTopicStatusCommand') {
      return this.setIndustryTopicStatus(command.input ?? {});
    }
    if (name === 'GetIndustryGeneralTopicCommand') {
      return this.getIndustryGeneralTopic(command.input ?? {});
    }
    if (name === 'UpsertIndustryGeneralTopicCommand') {
      return this.upsertIndustryGeneralTopic(command.input ?? {});
    }
    if (name === 'ListIndustryNewsSourcesCommand') {
      return this.listIndustryNewsSources(command.input ?? {});
    }
    if (name === 'UpsertIndustryNewsSourceCommand') {
      return this.upsertIndustryNewsSource(command.input ?? {});
    }
    if (name === 'SetIndustryNewsSourceStatusCommand') {
      return this.setIndustryNewsSourceStatus(command.input ?? {});
    }
    if (name === 'GetPipelineBubblesCommand') {
      // Grain-aware dispatch: the wire op returns different row shapes per
      // groupBy (identity fields differ; ACCOUNT grain populates
      // accountName/ucCount/freshness/bestDaysSince). The default fixture is
      // USE_CASE — served for any groupBy other than the ones we split out.
      const groupBy = (command.input?.groupBy as string | undefined) ?? 'use_case';
      if (groupBy === 'account') return structuredClone(getPipelineBubblesAccountMock);
      if (groupBy === 'industry') return structuredClone(getPipelineBubblesIndustryMock);
      return structuredClone(getPipelineBubblesMock);
    }
    const response = RESPONSES[name];
    if (response === undefined) {
      throw new Error(`[MockIvtHubClient] No mock response registered for ${name}`);
    }
    // Return a deep copy so a page mutating its result can't poison later reads.
    return structuredClone(response);
  }

  /** All-or-nothing like the service; aliases outside grantRosterMock exercise the InvalidInputException path. */
  private createGrants(input: Record<string, unknown>): unknown {
    const aliases = ((input.aliases as string[] | undefined) ?? []).map((a) => a.toLowerCase());
    const unknown = aliases.filter((a) => !grantRosterMock.includes(a));
    if (unknown.length > 0) {
      throw Object.assign(new Error(`Not IVT users: ${unknown.join(', ')}`), {
        name: 'InvalidInputException',
      });
    }
    const row = this.grants.find((g) => g.industry === input.industry);
    if (!row) {
      throw Object.assign(new Error(`No industry ${String(input.industry)}`), {
        name: 'ResourceNotFoundException',
      });
    }
    const have = new Set((row.grantees ?? []).map((g) => g.alias));
    const added = aliases
      .filter((a) => !have.has(a))
      .map((alias) => ({ alias, grantedBy: 'mock-admin', grantedAt: new Date() }));
    row.grantees = [...(row.grantees ?? []), ...added].sort((a, b) =>
      (a.alias ?? '').localeCompare(b.alias ?? ''),
    );
    return structuredClone({ industry: row });
  }

  /** Slice listTaggedPipelineMock.items by an offset cursor; echo full stageCounts.
   *  Applies sfdcPipelinePropertyFilter (the JSON-string tree) as an equivalence
   *  filter so tests exercise the frontend translate → backend-shape → filter path. */
  private paginateMotionPipeline(input: Record<string, unknown>): unknown {
    const full = structuredClone(listTaggedPipelineMock);
    let allItems = full.items ?? [];
    // Property-filter tree (Stage/Account/Oppty/Owner, one-level AND/OR). Backend
    // gets it as a JSON string; the mock parses + evaluates it against each row.
    allItems = applyPropertyFilter(allItems, input.sfdcPipelinePropertyFilter);
    const maxResults = typeof input.maxResults === 'number' && input.maxResults > 0 ? input.maxResults : 100;
    const start = typeof input.nextToken === 'string' ? Number.parseInt(input.nextToken, 10) || 0 : 0;
    const page = allItems.slice(start, start + maxResults);
    const nextStart = start + maxResults;
    return {
      stageCounts: full.stageCounts, // full set, stable across pages (铁律②)
      // NOTE: scoreOptions (the legacy distinct-score set) is NO LONGER produced here.
      // Score moved into the PropertyFilter tree (PIPELINE_SCORE); its candidate values
      // now come from getPipelineFilterOptions(PIPELINE_SCORE) below (DAT-8462 Phase 2).
      items: page,
      nextToken: nextStart < allItems.length ? String(nextStart) : undefined,
    };
  }

  /** GetPipelineFilterOptions mock: SFDC_STAGE → fixed 7-bucket set; identity fields
   *  → distinct values over the IVT-scoped rows, ILIKE substring + offset paging.
   *  Candidates are scoped by the IVT filters (industries/useCases/assetIds) the
   *  same way the backend's FILTER_WHERE narrows them (§4.6 cascade) — so mock mode
   *  and tests exercise the "select Industry → Opp options narrow" behavior. */
  private getPipelineFilterOptions(input: Record<string, unknown>): unknown {
    const field = String(input.field ?? '');
    const term = String(input.searchTerm ?? '').toLowerCase();
    // Stage's fixed 7-bucket set is also IVT-scoped on the backend; the mock keeps
    // it whole (fixture Stage coverage is sparse — scoping it adds no test value).
    if (field === 'SFDC_STAGE') {
      return { items: STAGE_BUCKET_OPTIONS, nextToken: undefined }; // whole set, no paging
    }
    // PIPELINE_SCORE: whole-UNIVERSE distinct scores (data-driven, NOT IVT-scoped —
    // score is non-sensitive and scoping would make a value vanish when an industry is
    // picked). value == label == the BARE number ("3"): the backend doesn't own the
    // score rubric, so the frontend hook formats "3分". Whole set, no paging.
    if (field === 'PIPELINE_SCORE') {
      const scores = [
        ...new Set(
          (listTaggedPipelineMock.items ?? []).map((r) => r.pipelineScore).filter((s): s is number => s != null),
        ),
      ].sort((a, b) => a - b);
      return { items: scores.map((n) => ({ value: String(n), label: String(n) })), nextToken: undefined };
    }
    const col = IDENTITY_FIELD_TO_ROW[field];
    if (!col) return { items: [], nextToken: undefined };
    const rows = (listTaggedPipelineMock.items ?? []).filter((r) => rowMatchesIvtScope(r, input));
    const distinct = [...new Set(rows.map((r) => r[col]).filter((v): v is string => typeof v === 'string' && v.length > 0))]
      .filter((v) => (term ? v.toLowerCase().includes(term) : true))
      .sort((a, b) => a.localeCompare(b));
    const maxResults = typeof input.maxResults === 'number' && input.maxResults > 0 ? input.maxResults : 50;
    const start = typeof input.nextToken === 'string' ? Number.parseInt(input.nextToken, 10) || 0 : 0;
    const page = distinct.slice(start, start + maxResults);
    const nextStart = start + maxResults;
    return {
      items: page.map((v) => ({ value: v, label: v })), // identity: value == label
      nextToken: nextStart < distinct.length ? String(nextStart) : undefined,
    };
  }

  /**
   * SearchAccounts mock — the service's contract, applied to `searchAccountsPool`:
   *   · `industries` narrows on each row's own industry; absent = every industry
   *     (the mock grants the caller all of them);
   *   · `searchTerm` is trimmed, then a case-insensitive substring of either name or a
   *     case-insensitive prefix of the id (so a 15-char SFDC id finds its 18-char
   *     account); blank = no constraint;
   *   · accountName ascending with the nameless last, then accountId;
   *   · offset paging whose token carries the (trimmed term, industries) it was issued
   *     for, and — like the real one — refuses with a 400 InvalidInputException ("does
   *     not match the current search") when replayed under another;
   *   · the model-level limits (maxResults 1..200, searchTerm ≤ 512) are 400
   *     ValidationException, Coral's own name for them.
   * The fingerprint check is the point of doing this rather than slicing: a hook that
   * forgets to drop its token on a new term fails here instead of paging quietly.
   */
  private searchAccounts(input: Record<string, unknown>): unknown {
    // Two exception names for one status, as on the real service: Coral's own model validation
    // (maxResults range, searchTerm length) throws ValidationException; the handler's checks
    // (token mismatch, input the database refuses) throw InvalidInputException. Callers are meant
    // to branch on the 400, never on the name — which is why the mock keeps them apart.
    const badRequest = (name: 'ValidationException' | 'InvalidInputException', message: string) =>
      Object.assign(new Error(message), { name, $metadata: { httpStatusCode: 400 } });
    const term = typeof input.searchTerm === 'string' ? input.searchTerm.trim().toLowerCase() : '';
    const industries = Array.isArray(input.industries)
      ? input.industries.filter((x): x is string => typeof x === 'string')
      : [];
    const maxResults = input.maxResults === undefined ? 50 : Number(input.maxResults);
    if (!Number.isInteger(maxResults) || maxResults < 1 || maxResults > 200) {
      throw badRequest('ValidationException', 'maxResults must be between 1 and 200');
    }
    if (typeof input.searchTerm === 'string' && input.searchTerm.length > 512) {
      throw badRequest('ValidationException', 'searchTerm must be at most 512 characters');
    }
    const fingerprint = JSON.stringify([term, [...industries].sort()]);
    let start = 0;
    if (typeof input.nextToken === 'string') {
      const sep = input.nextToken.indexOf('|');
      if (sep < 0 || input.nextToken.slice(sep + 1) !== fingerprint) {
        throw badRequest('InvalidInputException', 'nextToken does not match the current search');
      }
      start = Number.parseInt(input.nextToken.slice(0, sep), 10) || 0;
    }
    const matched = searchAccountsPool
      .filter((r) => industries.length === 0 || industries.includes(r.industry))
      .filter(
        (r) =>
          !term ||
          (r.accountName ?? '').toLowerCase().includes(term) ||
          (r.accountAkaName ?? '').toLowerCase().includes(term) ||
          (r.accountId ?? '').toLowerCase().startsWith(term),
      )
      .sort((a, b) => {
        if (a.accountName !== b.accountName) {
          if (a.accountName === undefined) return 1;
          if (b.accountName === undefined) return -1;
          return a.accountName < b.accountName ? -1 : 1;
        }
        return (a.accountId ?? '') < (b.accountId ?? '') ? -1 : 1;
      });
    const page = matched.slice(start, start + maxResults);
    const nextStart = start + maxResults;
    return {
      // The industry is the pool's routing column, not a member of the wire shape.
      items: page.map(({ industry: _industry, ...item }) => item),
      nextToken: nextStart < matched.length ? `${nextStart}|${fingerprint}` : undefined,
    };
  }

  destroy(): void {
    /* no-op: mock holds no sockets */
  }

  // ── Radar Admin (R16) ──────────────────────────────────────────────────────

  private radarIndustryKey(input: Record<string, unknown>): string {
    const industry = input.industry;
    if (typeof industry !== 'string' || !industry) {
      // Mirror the service's input-validation (@required industry on every
      // Admin op). The Admin page always sends it, so this is the test-only
      // defence against a hook that forgot to pass it through.
      throw Object.assign(new Error('industry is required'), {
        name: 'InvalidInputException',
      });
    }
    return industry;
  }

  private nextRadarId(prefix: string): string {
    this.radarIdCounter += 1;
    return `${prefix}-${String(this.radarIdCounter).padStart(4, '0')}`;
  }

  private radarClockCounter = 0;

  private radarNow(): Date {
    // Deterministic per-call monotonic timestamp. Advance on EVERY mutating
    // call so updates / status flips move updatedAt even when no new id is
    // minted — otherwise `pickLastUpdated` could not observe a change after
    // a pure Upsert on a loaded row. Clock lives on its own counter so a
    // mix of creates (`nextRadarId`) and updates still advances strictly
    // forward without interleaving.
    this.radarClockCounter += 1;
    return new Date(Date.UTC(2026, 9, 29, 0, this.radarClockCounter, 0));
  }

  private listIndustryTopics(input: Record<string, unknown>): unknown {
    const industry = this.radarIndustryKey(input);
    const requested = input.status as RadarConfigStatus | undefined;
    // Server defaults status to ACTIVE when the client omits it (topic-model
    // §3.2 ListIndustryTopics "省略默认 ACTIVE"). Pass INACTIVE to recover a
    // stopped topic from the Admin audit view (R16 does not surface it yet,
    // but the dispatch honors it so a later rev can read without a mock
    // change).
    const effective: RadarConfigStatus = requested ?? 'ACTIVE';
    const rows = (this.radarTopics[industry] ?? []).filter((t) => t.status === effective);
    const topics = [...rows].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    return structuredClone({ topics });
  }

  private getIndustryTopic(input: Record<string, unknown>): unknown {
    const industry = this.radarIndustryKey(input);
    const topicId = input.topicId as string | undefined;
    const row = (this.radarTopics[industry] ?? []).find((t) => t.topicId === topicId);
    if (!row) {
      throw Object.assign(new Error(`Topic ${String(topicId)} not found in ${industry}`), {
        name: 'ResourceNotFoundException',
      });
    }
    return structuredClone({ topic: row });
  }

  private upsertIndustryTopic(input: Record<string, unknown>): unknown {
    const industry = this.radarIndustryKey(input);
    const topicId = input.topicId as string | undefined;
    const draft = (input.draft ?? {}) as RadarStandardTopicDraft;
    const list = this.radarTopics[industry] ?? (this.radarTopics[industry] = []);
    const now = this.radarNow();
    if (topicId) {
      const row = list.find((t) => t.topicId === topicId);
      if (!row) {
        throw Object.assign(new Error(`Topic ${topicId} not found in ${industry}`), {
          name: 'ResourceNotFoundException',
        });
      }
      row.title = draft.title ?? row.title;
      row.searchKeywords = draft.searchKeywords ?? row.searchKeywords;
      row.excludeKeywords = draft.excludeKeywords ?? row.excludeKeywords;
      row.extractionRule = draft.extractionRule ?? row.extractionRule;
      row.inductionRule = draft.inductionRule ?? row.inductionRule;
      if (typeof draft.displayOrder === 'number') row.displayOrder = draft.displayOrder;
      row.updatedBy = 'mock-admin';
      row.updatedAt = now;
      return { topicId: row.topicId };
    }
    // Create — enforce the 10-ACTIVE/industry cap the service enforces
    // (topic-model §3.2) so the Add-topic button in the Admin page can be
    // tested against the same InvalidInputException the real backend returns.
    const activeCount = list.filter((t) => t.status === 'ACTIVE').length;
    if (activeCount >= 10) {
      throw Object.assign(
        new Error(`Industry ${industry} already has 10 ACTIVE STANDARD_TOPIC rows`),
        { name: 'InvalidInputException' },
      );
    }
    const defaultOrder = list.length
      ? Math.max(...list.map((t) => t.displayOrder ?? 0)) + 1
      : 1;
    const row: RadarStandardTopic = {
      topicId: this.nextRadarId('MOCK-RADAR-TOPIC'),
      title: draft.title ?? '',
      searchKeywords: draft.searchKeywords ?? [],
      excludeKeywords: draft.excludeKeywords ?? [],
      extractionRule: draft.extractionRule,
      inductionRule: draft.inductionRule,
      displayOrder: draft.displayOrder ?? defaultOrder,
      status: 'ACTIVE',
      createdBy: 'mock-admin',
      createdAt: now,
      updatedBy: 'mock-admin',
      updatedAt: now,
    };
    list.push(row);
    return { topicId: row.topicId };
  }

  private setIndustryTopicStatus(input: Record<string, unknown>): unknown {
    const industry = this.radarIndustryKey(input);
    const topicId = input.topicId as string | undefined;
    const status = input.status as RadarConfigStatus | undefined;
    const row = (this.radarTopics[industry] ?? []).find((t) => t.topicId === topicId);
    if (!row) {
      throw Object.assign(new Error(`Topic ${String(topicId)} not found in ${industry}`), {
        name: 'ResourceNotFoundException',
      });
    }
    if (status !== 'ACTIVE' && status !== 'INACTIVE') {
      throw Object.assign(new Error(`Invalid status ${String(status)}`), {
        name: 'InvalidInputException',
      });
    }
    // topic-model §2.7: v1 does NOT support INACTIVE→ACTIVE reactivation
    // once the topic has been INACTIVE'd (the §2.7 TOPIC_REMOVED directions
    // make the original identity unrecoverable). The mock mirrors the 409
    // the handler returns so the Admin page's reactivation path, if ever
    // added, lands on the same error.
    if (status === 'ACTIVE' && row.status === 'INACTIVE') {
      throw Object.assign(
        new Error('Reactivation (INACTIVE -> ACTIVE) is not supported in v1'),
        { name: 'ConflictException' },
      );
    }
    row.status = status;
    row.updatedBy = 'mock-admin';
    row.updatedAt = this.radarNow();
    return {};
  }

  private getIndustryGeneralTopic(input: Record<string, unknown>): unknown {
    const industry = this.radarIndustryKey(input);
    const row = this.radarGeneral[industry] ?? null;
    if (!row) {
      // Mirror the real service: topic-model §6 v1 only seeds LS_HC, so for
      // any other industry the handler SELECTs no row and throws 404 (the
      // Smithy's `generalTopic` is @required, so the wire shape has no "empty
      // body" fallback). The Admin page catches this specific exception in
      // radarConfigClient.getSnapshot and renders the "未初始化" warning.
      throw Object.assign(
        new Error(`GENERAL_TOPIC not seeded for ${industry}`),
        { name: 'ResourceNotFoundException', $metadata: { httpStatusCode: 404 } },
      );
    }
    return structuredClone({ generalTopic: row });
  }

  private upsertIndustryGeneralTopic(input: Record<string, unknown>): unknown {
    const industry = this.radarIndustryKey(input);
    const draft = (input.draft ?? {}) as RadarGeneralTopicDraft;
    const now = this.radarNow();
    const existing = this.radarGeneral[industry] ?? null;
    if (!existing) {
      // Mirror the handler: GENERAL_TOPIC is INSERTed by the M1 seed, not by
      // the Admin page. Upsert without a seeded row => a 500-equivalent error
      // (the handler SELECTs then UPDATEs; a missing row raises).
      throw Object.assign(
        new Error(
          `GENERAL_TOPIC not seeded for ${industry} — the one-off seed must run before Upsert`,
        ),
        { name: 'ResourceNotFoundException' },
      );
    }
    this.radarGeneral[industry] = {
      ...existing,
      searchKeywords: draft.searchKeywords ?? existing.searchKeywords,
      excludeKeywords: draft.excludeKeywords ?? existing.excludeKeywords,
      extractionRule: draft.extractionRule ?? existing.extractionRule,
      inductionRule: draft.inductionRule ?? existing.inductionRule,
      updatedBy: 'mock-admin',
      updatedAt: now,
    };
    return {};
  }

  private listIndustryNewsSources(input: Record<string, unknown>): unknown {
    const industry = this.radarIndustryKey(input);
    const requested = input.status as RadarConfigStatus | undefined;
    const effective: RadarConfigStatus = requested ?? 'ACTIVE';
    const sources = (this.radarNewsSources[industry] ?? []).filter(
      (s) => s.status === effective,
    );
    return structuredClone({ sources });
  }

  private upsertIndustryNewsSource(input: Record<string, unknown>): unknown {
    const industry = this.radarIndustryKey(input);
    const sourceId = input.sourceId as string | undefined;
    const draft = (input.draft ?? {}) as RadarNewsSourceDraft;
    const list = this.radarNewsSources[industry] ?? (this.radarNewsSources[industry] = []);
    const now = this.radarNow();
    if (sourceId) {
      const row = list.find((s) => s.sourceId === sourceId);
      if (!row) {
        throw Object.assign(new Error(`News source ${sourceId} not found in ${industry}`), {
          name: 'ResourceNotFoundException',
        });
      }
      row.title = draft.title ?? row.title;
      if (draft.sourceKind) row.sourceKind = draft.sourceKind;
      // sourceLocator presence: topic-model §2.1 CHECK requires it when
      // GOOGLE_NEWS_SITE and forbids it when GOOGLE_NEWS_KEYWORD; the mock
      // clears it on the keyword branch instead of leaving the stale value.
      if (row.sourceKind === 'GOOGLE_NEWS_SITE') {
        row.sourceLocator = draft.sourceLocator ?? row.sourceLocator;
      } else {
        row.sourceLocator = undefined;
      }
      if (draft.sourceLocale) row.sourceLocale = draft.sourceLocale;
      row.updatedBy = 'mock-admin';
      row.updatedAt = now;
      return { sourceId: row.sourceId };
    }
    const kind = draft.sourceKind ?? 'GOOGLE_NEWS_KEYWORD';
    const row: RadarNewsSource = {
      sourceId: this.nextRadarId('MOCK-RADAR-NEWS-SRC'),
      title: draft.title ?? '',
      sourceKind: kind,
      sourceLocator: kind === 'GOOGLE_NEWS_SITE' ? draft.sourceLocator : undefined,
      sourceLocale: draft.sourceLocale ?? 'zh-CN',
      status: 'ACTIVE',
      createdBy: 'mock-admin',
      createdAt: now,
      updatedBy: 'mock-admin',
      updatedAt: now,
    };
    list.push(row);
    return { sourceId: row.sourceId };
  }

  private setIndustryNewsSourceStatus(input: Record<string, unknown>): unknown {
    const industry = this.radarIndustryKey(input);
    const sourceId = input.sourceId as string | undefined;
    const status = input.status as RadarConfigStatus | undefined;
    const row = (this.radarNewsSources[industry] ?? []).find((s) => s.sourceId === sourceId);
    if (!row) {
      throw Object.assign(new Error(`News source ${String(sourceId)} not found in ${industry}`), {
        name: 'ResourceNotFoundException',
      });
    }
    if (status !== 'ACTIVE' && status !== 'INACTIVE') {
      throw Object.assign(new Error(`Invalid status ${String(status)}`), {
        name: 'InvalidInputException',
      });
    }
    row.status = status;
    row.updatedBy = 'mock-admin';
    row.updatedAt = this.radarNow();
    return {};
  }
}
